import React, { useState, useEffect } from 'react';
import { getSiteInfo } from '../services/api';

const Header = ({ user, onLogout, sessionTimeLeft, onExtendSession, isAuthenticated }) => {
  const [siteInfo, setSiteInfo] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Get user display name with multiple fallbacks
  const getUserDisplayName = () => {
    if (user?.display_name) return user.display_name;
    if (user?.name) return user.name;
    if (user?.username) return user.username;
    if (user?.user_login) return user.user_login;
    const storedUsername = localStorage.getItem('wp_username');
    if (storedUsername) return storedUsername;
    return 'User';
  };

  useEffect(() => {
    const fetchSiteInfo = async () => {
      try {
        const info = await getSiteInfo();
        setSiteInfo(info);
      } catch (error) {
      }
    };

    fetchSiteInfo();
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <header className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-row justify-between items-center py-4 sm:py-6 gap-4 sm:gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center">
            <div className="flex items-center">
              <img 
                src="/binary-logo.svg"
                alt="Binary Code Logo"
                className="h-8 w-auto mr-2"
              />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">August CMS</h1>
            </div>
            <span className="mt-2 sm:mt-0 sm:ml-3 px-3 py-1 text-xs sm:text-sm font-medium bg-primary-100 text-primary-800 rounded-full self-start">
              Content Integration
            </span>
          </div>
          
          <div className="flex flex-row items-center space-x-2 sm:space-x-4">
            {/* Session Timer */}
            {isAuthenticated && sessionTimeLeft > 0 && (
              <>
                {/* Desktop Session Timer */}
                <div className="hidden sm:flex items-center space-x-2 px-4 py-3 bg-gray-50 rounded-lg border shadow-sm">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Session: {Math.floor(sessionTimeLeft / 60000)}:{(Math.floor(sessionTimeLeft / 1000) % 60).toString().padStart(2, '0')}
                  </span>
                  <button
                    onClick={onExtendSession}
                    className="ml-2 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-200 shadow-sm"
                  >
                    Extend
                  </button>
                </div>
                
                {/* Mobile Session Timer */}
                <div className="sm:hidden flex items-center px-2 py-1 bg-primary-100 rounded-md">
                  <svg className="w-3 h-3 text-primary-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs font-medium text-primary-700">
                    {Math.floor(sessionTimeLeft / 60000)}:{(Math.floor(sessionTimeLeft / 1000) % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              </>
            )}
            
            {/* User Info */}
            {user && (
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shadow-sm overflow-hidden bg-white border border-gray-200">
                  <img 
                    src="/AdminAvatar.png"
                    alt="Admin Avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="hidden sm:block text-sm">
                  <p className="font-medium text-gray-900">{getUserDisplayName()}</p>
                  <p className="text-gray-500 text-xs">Web Admin</p>
                </div>
              </div>
            )}
            
            {/* Logout Button */}
            {user && (
              <div className="flex items-center">
                <button
                  onClick={handleLogoutClick}
                  className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-4 sm:p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Confirm Logout</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to logout? You will need to login again to access the system.
                </p>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={handleLogoutCancel}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
